import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { palette } from "@evergis/ui";
import { BarChartMarshalledGroup } from "@evergis/charts";

import { BarChart, TooltipContainer, TooltipDescription } from "./styled";

import { declension } from "../../../../utils/declension";
import { formatNumber } from "../../../../utils/formatNumber";

import { ClassifyChartProps } from "./types";

export const ClassifyChart: FC<ClassifyChartProps> = ({ data, classifyData, isNumberValue }) => {
  const { t } = useTranslation("table");

  const customXAxis = useCallback(xAxis => {
    xAxis.remove();
  }, []);

  const customYAxis = useCallback(yAxis => {
    yAxis.remove();
  }, []);

  const customBars = useCallback(({ bars }) => {
    bars.attr("rx", 1).attr("ry", 1);
  }, []);

  const marshalledMap = useCallback(items => {
    return items.map(([item]: BarChartMarshalledGroup[]) => {
      if (item.height < 2 && item.value !== 0) {
        return [
          {
            ...item,
            color: palette.primary,
            height: 2,
            y: 22,
            rx: 1,
            ry: 1,
          },
        ];
      }

      if (item.value === 0) {
        return [
          {
            ...item,
            color: "#E2E2E2",
            height: 2,
            y: 22,
            rx: 1,
            ry: 1,
          },
        ];
      }

      return [{ ...item }];
    });
  }, []);

  return (
    <BarChart
      colors={{ value: palette.primary }}
      width={72}
      height={24}
      data={data}
      customXAxis={customXAxis}
      customYAxis={customYAxis}
      xAxisPadding={0}
      yAxisPadding={0}
      customBars={customBars}
      dynamicTooltipEnable
      barWidth={4}
      marshalledMap={marshalledMap}
      renderTooltip={groups => {
        const group = groups[0];

        if (isNumberValue) {
          const fromValue = classifyData.classifyResult[group.groupIndex].value;
          const toValue = classifyData.classifyResult[group.groupIndex + 1].value;
          const formattedFrom = formatNumber(Math.round(fromValue * 100) / 100);
          const formattedTo = formatNumber(Math.round(toValue * 100) / 100);

          return (
            <TooltipContainer>
              {group.value} {declension(t("feature1"), t("feature2"), t("feature3"), group.value)}
              {classifyData && <TooltipDescription>{`${formattedFrom} - ${formattedTo}`}</TooltipDescription>}
            </TooltipContainer>
          );
        }

        return (
          <TooltipContainer>
            {group.value} {declension(t("feature1"), t("feature2"), t("feature3"), group.value)}
            <TooltipDescription>{group.groupName}</TooltipDescription>
          </TooltipContainer>
        );
      }}
      margin={{
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
      }}
    />
  );
};
